import React from 'react'
import gql from 'graphql-tag'
import hoistNonReactStatics from 'hoist-non-react-statics'
import { Subtract } from 'utility-types'

import {
  ActionItemType,
  MergeTaggable,
  ProjectType,
  TaskType,
  useUseMergeTagsMergeTagsQuery,
  UseMergeTagsMergeTagsQueryHookResult,
  useUseMergeTagsProjectMergeTagsQuery,
} from '~/common/types/graphql'
import useProjectId from '~/views/hooks/useProjectId'
import { useRouter } from 'next/router'
import useSearchParams from '~/views/hooks/useSearchParams'
import { useSession } from 'shared/contexts/session-provider/session-provider'

interface Params {
  taggable: MergeTaggable
  withValues?: boolean
  projectId?: ProjectType['id']
  taskId?: TaskType['id']
  customizedOnly?: boolean
  skip?: boolean
}

const useMergeTags = ({
  taggable,
  withValues = false,
  projectId: projectIdProp,
  taskId: taskIdProp,
  customizedOnly = false,
  skip = false,
}: Params) => {
  const { session } = useSession()
  const { pathname } = useRouter()
  const inTemplateView = pathname?.includes('/app/templates')
  const inSharedProjectView = pathname?.includes('/shared-project/')

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const urlProjectId = inTemplateView ? undefined : useProjectId()
  const projectId = projectIdProp ? projectIdProp : urlProjectId
  const shouldSkip =
    skip || (withValues && !projectId) || inSharedProjectView || !session
  const shouldValuesSkip = !projectId || !withValues || !session
  const params = useSearchParams()
  const { 'task-id': taskId, 'action-item-id': actionItemId } = (params ||
    {}) as {
    'action-item-id': ActionItemType['id'] | undefined
    'task-id': TaskType['id'] | undefined
  }

  const taskIdVar = taskIdProp || taskId

  const {
    data: { mergeTagOptions } = {},
    loading: mergeTagOptionsLoading,
    refetch: refetchOptions,
  } = useUseMergeTagsMergeTagsQuery({
    variables: {
      taggable: taggable,
      projectId: projectId || null,
    },
    skip: shouldSkip,
  })

  const variables = {
    projectId: projectId || '',
    taggable,
    taggableId: actionItemId || taskIdVar,
    customizedOnly,
  }

  const {
    data: { mergeTagValues } = {},
    loading: mergeTagValuesLoading,
    refetch,
  } = useUseMergeTagsProjectMergeTagsQuery({
    variables,
    skip: shouldValuesSkip,
  })

  const refetchMergeTagValues = () => {
    refetch(variables)
  }

  const refetchMergeTagOptions = () => {
    refetchOptions({
      taggable: taggable,
      projectId,
    })
  }

  return {
    mergeTagOptions,
    mergeTagOptionsLoading,
    mergeTagValues,
    mergeTagValuesLoading,
    refetchMergeTagValues,
    refetchMergeTagOptions,
  }
}

interface WithUseMergeTagsProps {
  withMergeTags: UseMergeTagsMergeTagsQueryHookResult
}

export const withUseMergeTags = <Props extends WithUseMergeTagsProps>(
  Component: React.ComponentType<Props>,
) => {
  const WithMergeTagsComponents = React.forwardRef<
    any,
    Subtract<Props, WithUseMergeTagsProps>
  >(({ ...props }, ref) => {
    const withValues = Boolean((props as any).edit) && (props as any).isProject
    const withMergeTags = useMergeTags({
      taggable: MergeTaggable.Task,
      withValues,
      projectId: (props as any).projectId,
    })

    return (
      <Component
        {...(props as unknown as Props)}
        ref={ref}
        withMergeTags={withMergeTags}
      />
    )
  })

  return hoistNonReactStatics(WithMergeTagsComponents, Component)
}

gql`
  query UseMergeTags_MergeTags($projectId: ID, $taggable: MergeTaggable!) {
    mergeTagOptions(projectId: $projectId, taggable: $taggable) {
      id
      name
      slug
      external
    }
  }
`

gql`
  query UseMergeTags_ProjectMergeTags(
    $projectId: ID!
    $taggable: MergeTaggable!
    $taggableId: ID
    $customizedOnly: Boolean!
  ) {
    mergeTagValues(
      projectId: $projectId
      taggable: $taggable
      taggableId: $taggableId
      customizedOnly: $customizedOnly
    ) {
      id
      value
      mergeTag {
        id
        slug
        name
        external
      }
    }
  }
`

export default useMergeTags
